const CHARS = 'ZION01アイウエオカキクケコサシスセソ0123456789'
const WORDS = ['ZION', 'ZIONYX', 'MATRIX', 'NEURAL', 'CIPHER', 'SIGNAL', 'ONYX', 'PRIVACY', 'SOCKET', 'ENCRYPT', 'GHOST']
const CHAR_H = 18
const COL_W = 18
/** Opacità base +20% precedente +10% attuale */
const OPACITY_SCALE = 1.2 * 1.1

interface Column {
  x: number
  headY: number
  speed: number
  glyphs: string[]
}

let canvas: HTMLCanvasElement | null = null
let ctx: CanvasRenderingContext2D | null = null
let columns: Column[] = []
let frameId = 0
let running = false
let viewW = 0
let viewH = 0

function randomChar(): string {
  return CHARS[Math.floor(Math.random() * CHARS.length)]!
}

function buildTrail(): string[] {
  const len = 6 + Math.floor(Math.random() * 7)
  if (Math.random() < 0.4) {
    const word = WORDS[Math.floor(Math.random() * WORDS.length)]!
    const chars = word.split('')
    while (chars.length < len) chars.unshift(randomChar())
    return chars.slice(-len)
  }
  return Array.from({ length: len }, () => randomChar())
}

function createColumn(x: number, viewportH: number): Column {
  return {
    x,
    headY: Math.random() * viewportH,
    speed: Math.random() * 2.5 + 1.2,
    glyphs: buildTrail(),
  }
}

function glyphAlpha(index: number, total: number): number {
  const fade = 0.22 + (index / Math.max(total - 1, 1)) * 0.78
  return (0.06 + 0.14 * fade) * OPACITY_SCALE
}

function viewportMetrics(): { w: number; h: number; top: number; left: number } {
  const vv = window.visualViewport
  return {
    w: vv?.width ?? window.innerWidth,
    h: vv?.height ?? window.innerHeight,
    top: vv?.offsetTop ?? 0,
    left: vv?.offsetLeft ?? 0,
  }
}

function resize(): void {
  if (!canvas || !ctx) return
  const { w, h, top, left } = viewportMetrics()
  viewW = w
  viewH = h

  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = Math.floor(w * dpr)
  canvas.height = Math.floor(h * dpr)
  canvas.style.top = `${top}px`
  canvas.style.left = `${left}px`
  canvas.style.width = `${w}px`
  canvas.style.height = `${h}px`
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

  const count = Math.ceil(w / COL_W)
  columns = Array.from({ length: count }, (_, i) => createColumn(i * COL_W + 2, h))
}

function tick(): void {
  if (!running || !canvas || !ctx) return

  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, viewW, viewH)

  ctx.font = '15px ui-monospace, monospace'

  for (const col of columns) {
    col.headY += col.speed
    const trailPx = col.glyphs.length * CHAR_H
    if (col.headY > viewH + trailPx) {
      col.headY = -trailPx - Math.random() * viewH * 0.3
      col.speed = Math.random() * 2.5 + 1.2
      col.glyphs = buildTrail()
    }

    col.glyphs[col.glyphs.length - 1] = randomChar()

    for (let i = 0; i < col.glyphs.length; i++) {
      const y = col.headY - (col.glyphs.length - 1 - i) * CHAR_H
      if (y < -CHAR_H || y > viewH + CHAR_H) continue
      const ch = col.glyphs[i]!
      const alpha = glyphAlpha(i, col.glyphs.length)
      ctx.fillStyle = `rgba(0, 255, 65, ${alpha})`
      ctx.fillText(ch, col.x, y)
    }
  }

  frameId = requestAnimationFrame(tick)
}

function onViewportChange(): void {
  resize()
}

export function startMatrixRain(): void {
  if (running) return
  running = true

  canvas = document.createElement('canvas')
  canvas.id = 'matrix-rain'
  canvas.setAttribute('aria-hidden', 'true')
  document.body.prepend(canvas)

  ctx = canvas.getContext('2d')
  if (!ctx) {
    stopMatrixRain()
    return
  }

  resize()
  window.addEventListener('resize', onViewportChange)
  window.visualViewport?.addEventListener('resize', onViewportChange)
  window.visualViewport?.addEventListener('scroll', onViewportChange)
  frameId = requestAnimationFrame(tick)
}

export function stopMatrixRain(): void {
  running = false
  cancelAnimationFrame(frameId)
  window.removeEventListener('resize', onViewportChange)
  window.visualViewport?.removeEventListener('resize', onViewportChange)
  window.visualViewport?.removeEventListener('scroll', onViewportChange)
  canvas?.remove()
  canvas = null
  ctx = null
  columns = []
  viewW = 0
  viewH = 0
}

export function syncMatrixRain(theme: string): void {
  if (theme === 'matrix') startMatrixRain()
  else stopMatrixRain()
}
